import { fetchConfirmUpload, fetchPresignUpload } from '../service/api';
import { getAssetFileType, getFileExtension } from './asset-file-types';
import { calculateFileMd5 } from './crypto';

/** 上传图片最小宽高 (像素) */
export const MIN_IMAGE_DIMENSION = 1;

/** 上传图片最大宽高 (像素) */
export const MAX_IMAGE_DIMENSION = 6000;

/** 固定访问策略路由类型 */
export type AccessPolicyRoute = Api.Upload.AccessPolicyRoute;

/**
 * 访问策略路由配置表 (对齐参考标准 oss_upload_client.html)
 */
export const POLICY_ROUTES: Record<
  AccessPolicyRoute,
  { name: string; zhName: string; scenes: string[]; text: string; backendOnly?: boolean }
> = {
  'uploader-only': {
    name: 'UPLOADER_ONLY',
    zhName: '仅上传者可访问',
    scenes: ['SUBJECT', 'APPLICATION', 'CATALOG'],
    text: '私有材料。前台仅上传本人可续签；后台审核人员可查看。'
  },
  'public-when-published': {
    name: 'OWNER_OR_PUBLIC_WHEN_PUBLISHED',
    zhName: '所有者或发布后公开',
    scenes: ['ACCOUNT', 'PROFILE', 'DEMAND', 'SOLUTION', 'APPLICATION', 'CATALOG', 'LEARNING_RESOURCE', 'BANNER'],
    text: '用于可能对外展示的内容。是否对公众开放由所选业务模块的发布/启用状态决定。'
  },
  'parent-visible': {
    name: 'OWNER_OR_PARENT_VISIBLE',
    zhName: '所有者或父对象可见时访问',
    scenes: ['ACCOUNT', 'PROFILE', 'COMMUNITY', 'COMMENT', 'SUPPORT_TICKET'],
    text: '用于跟随具体业务对象可见性的附件。能否续签由该对象的真实访问权限决定。'
  },
  'resource-participant': {
    name: 'RESOURCE_PARTICIPANT',
    zhName: '业务资源参与方可访问',
    scenes: ['LEAD', 'PROJECT', 'CONTRACT', 'REVENUE'],
    text: '用于合作和结算资料。只有真实业务参与方以及具备权限的后台人员可访问。'
  },
  'entitled': {
    name: 'OWNER_OR_ENTITLED',
    zhName: '所有者或权益用户可访问',
    scenes: ['CATALOG', 'LEARNING_RESOURCE'],
    text: '用于需要购买、授权或会员权益的内容和学习资料。'
  },
  'backend-only': {
    name: 'BACKEND_ONLY',
    zhName: '仅后台可访问',
    scenes: ['APPLICATION', 'PROJECT', 'CONTRACT', 'REVENUE', 'LEARNING_RESOURCE', 'BANNER', 'INTERNAL'],
    text: '平台内部材料。前台没有上传路由，也不能取得访问地址。',
    backendOnly: true
  }
};

/**
 * 业务场景（scene）详细说明配置表 (对齐参考标准 oss_upload_client.html)
 */
export const SCENE_DETAILS: Record<string, { label: string; tip: string }> = {
  SUBJECT: {
    label: '主体认证：身份证、营业执照、授权书',
    tip: '个人/企业主体认证附件。选择“仅上传者可访问”，文件仅认证申请人和后台审核人员可查看。'
  },
  ACCOUNT: {
    label: '账号模块：用户头像',
    tip: '用户账号头像。对外展示使用“所有者或发布后公开”；私有账号资料才使用“父对象可见时访问”。'
  },
  PROFILE: {
    label: '服务商资料：展示图、作品集',
    tip: '服务商主页展示图、作品集图片/视频/文档。公开资料选择“所有者或发布后公开”。'
  },
  COMMUNITY: {
    label: '社区模块：帖子配图、帖子视频',
    tip: '社区帖子媒体。必须选择“所有者或父对象可见时访问”，是否可看随帖子可见性和社区规则决定。'
  },
  COMMENT: {
    label: '评论模块：评论图片',
    tip: '评论图片。必须选择“所有者或父对象可见时访问”，不能使用社区帖子或头像资产代替。'
  },
  SUPPORT_TICKET: {
    label: '系统工单：描述附件、用户/客服沟通附件',
    tip: '工单描述和反馈附件。必须选择“所有者或父对象可见时访问”；工单用户可看自己的工单，后台可处理；后台内部回复附件不会向用户签发地址。'
  },
  DEMAND: {
    label: '供需市场：需求附件',
    tip: '需求发布的对外附件。选择“所有者或发布后公开”，发布后是否公开由需求状态决定。'
  },
  SOLUTION: {
    label: '供需市场：方案附件',
    tip: '方案发布的对外附件。选择“所有者或发布后公开”，发布后是否公开由方案状态决定。'
  },
  LEAD: {
    label: '合作线索：线索及跟进附件',
    tip: '合作线索和跟进材料。必须选择“业务资源参与方可访问”，仅线索参与方和后台人员可看。'
  },
  APPLICATION: {
    label: '发行服务：申请封面、海报、授权链、剧集源文件',
    tip: '发行申请的公开封面/海报可选择“所有者或发布后公开”；版权证明、授权链、剧集源文件选择“仅上传者可访问”；内部审查材料使用后台专用策略。'
  },
  PROJECT: {
    label: '项目服务：项目剧集、项目内部附件',
    tip: '项目剧集和项目资料。参与方资料选择“业务资源参与方可访问”；平台内部资料仅后台可访问。'
  },
  CONTRACT: {
    label: '合同服务：签署件、合同补充材料',
    tip: '合同签署件和补充材料。合同参与方资料选择“业务资源参与方可访问”；平台草稿和完整签署归档仅后台可访问。'
  },
  REVENUE: {
    label: '营收服务：报表、结算和付款凭证',
    tip: '营收原始报表、结算和付款凭证。参与方资料选择“业务资源参与方可访问”；平台财务内部资料仅后台可访问。'
  },
  CATALOG: {
    label: '内容商城：作品封面、预告片、正文、授权链',
    tip: '作品封面/预告片等公开素材选择“所有者或发布后公开”；购买、授权后才能查看的正文/下载资料选择“所有者或权益用户可访问”；私有证明材料选择“仅上传者可访问”。'
  },
  LEARNING_RESOURCE: {
    label: '学习资源：封面、课时视频、下载资料',
    tip: '免费且已发布的封面/简介选择“所有者或发布后公开”；付费课程、课时附件和下载资料选择“所有者或权益用户可访问”；课程草稿和内部编辑材料仅后台可访问。'
  },
  BANNER: {
    label: '运营模块：轮播图素材',
    tip: '可投放轮播图选择“所有者或发布后公开”，是否对外展示由轮播图启用和投放时间决定；永不投放的运营素材仅后台可访问。'
  },
  INTERNAL: {
    label: '平台内部：运营内部素材',
    tip: '仅平台后台使用的内部素材。必须选择“仅后台可访问”。'
  }
};

/**
 * 根据业务场景 scene 自动推导前台推荐的访问策略路由 (policy)
 * 策略由路由决定，客户端提交时不会在请求体中附带 access_policy、OSS 路径或 bucket。
 */
export function getPolicyByScene(scene: Api.Upload.UploadScene | string): AccessPolicyRoute {
  const normalizedScene = String(scene || '').toUpperCase();

  switch (normalizedScene) {
    case 'COMMUNITY':
    case 'COMMENT':
    case 'COMMENT_IMAGE':
    case 'SUPPORT_TICKET':
      return 'parent-visible';

    case 'LEAD':
    case 'PROJECT':
    case 'CONTRACT':
    case 'REVENUE':
      return 'resource-participant';

    case 'SUBJECT':
      return 'uploader-only';

    case 'ACCOUNT':
    case 'PROFILE':
    case 'DEMAND':
    case 'SOLUTION':
    case 'APPLICATION':
    case 'CATALOG':
    case 'LEARNING_RESOURCE':
    case 'BANNER':
    case 'COMMON':
    case 'IMAGE':
    case 'COURSE':
    default:
      return 'public-when-published';
  }
}

export interface PreviewImage {
  id: string;
  url: string;
  file?: File;
  file_ext?: string;
  file_name?: string;
  file_size?: number;
  file_type?: string;
  is_object_url?: boolean;
  object_key?: string;
  upload_asset_id?: string | number;
  asset_id?: string;
  unique_id?: string;
  source_name?: string;
  asset_source?: 'generated_asset' | 'upload_asset';
}

export interface FileMediaMetadata {
  width?: number;
  height?: number;
  duration_ms?: number;
}

/**
 * 清洗直传 OSS 的 Header，过滤保留 Header
 */
export function sanitizeUploadHeaders(headers?: Record<string, string>) {
  if (!headers) {
    return {};
  }

  const blockedHeaders = new Set(['host', 'content-length', 'connection']);
  const nextHeaders: Record<string, string> = {};

  Object.entries(headers).forEach(([key, value]) => {
    if (!blockedHeaders.has(key.toLowerCase())) {
      nextHeaders[key] = value;
    }
  });

  return nextHeaders;
}

/**
 * 释放 Blob URL
 */
export function revokeImageUrl(image?: PreviewImage | null) {
  if (image?.is_object_url && image.url.startsWith('blob:')) {
    URL.revokeObjectURL(image.url);
  }
}

/**
 * 从本地 File 对象创建 PreviewImage 结构
 */
export function createPreviewImageFromFile(file: File): PreviewImage {
  const nameExt = file.name.includes('.') ? file.name.split('.').pop() : '';
  const mimeExt = file.type.includes('/') ? file.type.split('/').pop() : '';
  const file_ext = nameExt || mimeExt || '';
  const file_name = file.name || (file_ext ? `pasted-image.${file_ext}` : 'pasted-image');

  return {
    id: Math.random().toString(36).slice(2, 9),
    url: URL.createObjectURL(file),
    file,
    file_ext,
    file_name,
    source_name: file_name,
    file_size: file.size,
    file_type: file.type || 'image',
    is_object_url: true
  };
}

function isImageFile(file: File) {
  const fileType = String(file.type || '').toLowerCase();
  const fileExtension = getFileExtension(file.name);

  return fileType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg'].includes(fileExtension);
}

/**
 * 读取图片尺寸
 */
export function readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
    };

    image.onload = () => {
      const dimensions = {
        width: image.naturalWidth,
        height: image.naturalHeight
      };
      cleanup();

      if (dimensions.width > 0 && dimensions.height > 0) {
        resolve(dimensions);
      } else {
        resolve(null);
      }
    };
    image.onerror = () => {
      cleanup();
      resolve(null);
    };
    image.src = objectUrl;
  });
}

/**
 * 读取视频元数据（宽度、高度与时长毫秒数）
 */
export function readVideoMetadata(file: File): Promise<{ width: number; height: number; duration_ms: number } | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    const objectUrl = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(objectUrl);

    video.onloadedmetadata = () => {
      const width = video.videoWidth;
      const height = video.videoHeight;
      const duration_ms = Math.round(video.duration * 1000);
      cleanup();
      resolve({ width, height, duration_ms });
    };
    video.onerror = () => {
      cleanup();
      resolve(null);
    };
    video.src = objectUrl;
  });
}

/**
 * 读取音频时长（毫秒数）
 */
export function readAudioDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const audio = new Audio();
    audio.preload = 'metadata';
    const objectUrl = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(objectUrl);

    audio.onloadedmetadata = () => {
      const duration_ms = Math.round(audio.duration * 1000);
      cleanup();
      resolve(duration_ms);
    };
    audio.onerror = () => {
      cleanup();
      resolve(null);
    };
    audio.src = objectUrl;
  });
}

/**
 * 自动识别并提取文件媒体元数据 (图片/视频/音频)
 */
export async function getFileMediaMetadata(file: File): Promise<FileMediaMetadata> {
  const mimeType = String(file.type || '').toLowerCase();

  if (mimeType.startsWith('image/')) {
    const dims = await readImageDimensions(file);
    return { width: dims?.width, height: dims?.height };
  }

  if (mimeType.startsWith('video/')) {
    const videoInfo = await readVideoMetadata(file);
    return {
      width: videoInfo?.width,
      height: videoInfo?.height,
      duration_ms: videoInfo?.duration_ms
    };
  }

  if (mimeType.startsWith('audio/')) {
    const duration = await readAudioDuration(file);
    return { duration_ms: duration || undefined };
  }

  return {};
}

/**
 * 校验图片的宽高尺寸限制（非图片跳过）
 */
export async function validateImageDimensions(file: File) {
  if (!isImageFile(file)) {
    return undefined;
  }

  const dimensions = await readImageDimensions(file);
  if (!dimensions) {
    return undefined;
  }

  const { width, height } = dimensions;

  if (width < MIN_IMAGE_DIMENSION || height < MIN_IMAGE_DIMENSION) {
    throw new Error(`图片最小边不能小于 ${MIN_IMAGE_DIMENSION} 像素`);
  }

  if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
    throw new Error(`图片最大边不能超过 ${MAX_IMAGE_DIMENSION} 像素`);
  }

  return dimensions;
}

/**
 * 使用 XMLHttpRequest 直传 OSS (完全参照 reference oss_upload_client.html)
 */
export function uploadToOssWithXhr(
  uploadUrl: string,
  method: string,
  headers: Record<string, string> = {},
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          onProgress(pct);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        const detail = xhr.responseText ? `: ${xhr.responseText}` : '';
        reject(new Error(`OSS 直传失败 [状态码 ${xhr.status}]${detail}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('OSS 直传网络错误')));
    xhr.addEventListener('abort', () => reject(new Error('OSS 直传已取消')));

    xhr.open(method || 'PUT', uploadUrl);

    // 绑定并设置预签名 Headers
    const putHeaders = sanitizeUploadHeaders(headers);
    for (const [k, v] of Object.entries(putHeaders)) {
      xhr.setRequestHeader(k, v);
    }

    xhr.send(file);
  });
}

/**
 * 上传文件到 OSS，直接返回后端统一接口数据结构 Api.Upload.PresignedUploadResponse
 *
 * 流程参照 reference oss_upload_client.html：
 * 1. 自动提取媒体元数据 (图片/视频宽高、视频/音频时长 duration_ms)
 * 2. 计算文件 MD5 散列
 * 3. 确定访问策略路由 (policy)：若未指定 policy 则通过 getPolicyByScene(scene) 自动推导
 * 4. 请求 POST /assets/uploads/${policy}/presign 获取预签名上传地址及直传 Header
 * 5. 如果秒传/已存在 (already_uploaded)，直接返回 presignData
 * 6. 使用 XMLHttpRequest 直传 OSS (PUT/POST 请求)
 * 7. 请求 POST /assets/uploads/{asset_id}/confirm 确认完成
 *
 * @param file 待上传的文件
 * @param scene 上传场景，默认为 'COMMON'
 * @param policy 可选的显式访问策略路由，若未指定则自动推导
 * @param remark 可选的文件备注信息
 * @param onProgress 可选的直传进度回调函数
 */
export async function uploadFile(
  file: File,
  scene: Api.Upload.UploadScene = 'COMMON',
  policy?: AccessPolicyRoute,
  remark?: string,
  onProgress?: (percent: number) => void
): Promise<Api.Upload.PresignedUploadResponse> {
  const metadata = await getFileMediaMetadata(file);
  const fileMd5 = await calculateFileMd5(file);
  const fileType = getAssetFileType(file.name, file.type);
  const activePolicy = policy || getPolicyByScene(scene);

  // 如果是图片，校验尺寸范围
  if (isImageFile(file) && metadata.width && metadata.height) {
    if (metadata.width < MIN_IMAGE_DIMENSION || metadata.height < MIN_IMAGE_DIMENSION) {
      throw new Error(`图片最小边不能小于 ${MIN_IMAGE_DIMENSION} 像素`);
    }
    if (metadata.width > MAX_IMAGE_DIMENSION || metadata.height > MAX_IMAGE_DIMENSION) {
      throw new Error(`图片最大边不能超过 ${MAX_IMAGE_DIMENSION} 像素`);
    }
  }

  const payload: Api.Upload.CreatePresignedUploadRequest = {
    scene,
    file_type: fileType,
    file_name: file.name,
    file_size: file.size,
    mime_type: file.type || 'application/octet-stream',
    file_md5: fileMd5,
    file_remark: remark
  };

  if (metadata.width !== undefined && metadata.width !== null) {
    payload.file_width = metadata.width;
  }
  if (metadata.height !== undefined && metadata.height !== null) {
    payload.file_height = metadata.height;
  }
  if (metadata.duration_ms !== undefined && metadata.duration_ms !== null) {
    payload.duration_ms = metadata.duration_ms;
  }

  let presignData: Api.Upload.PresignedUploadResponse;
  try {
    presignData = await fetchPresignUpload(activePolicy, payload);
  } catch (err) {
    throw new Error(err instanceof Error ? err.message : '获取上传预签名失败');
  }

  if (!presignData) {
    throw new Error('获取上传预签名失败');
  }

  const assetId = presignData.asset_id || presignData.upload_asset_id;

  if (presignData.already_uploaded) {
    if (!assetId) {
      throw new Error('已上传文件资源ID缺失');
    }

    return {
      ...presignData,
      already_uploaded: true,
      file_name: presignData.file_name || file.name,
      file_ext: presignData.file_ext || getFileExtension(file.name),
      file_size: presignData.file_size || file.size,
      file_type: presignData.file_type || fileType,
      object_key: presignData.object_key || '',
      upload_asset_id: assetId,
      file_url: presignData.file_url || '',
      status: presignData.status || 'AVAILABLE',
      file_width: presignData.file_width || metadata.width,
      file_height: presignData.file_height || metadata.height,
      duration_ms: presignData.duration_ms || metadata.duration_ms
    };
  }

  if (!presignData.upload_url) {
    throw new Error('上传地址缺失');
  }

  // 直传 OSS (完全对齐 reference oss_upload_client.html)
  await uploadToOssWithXhr(
    presignData.upload_url,
    presignData.method || 'PUT',
    presignData.headers,
    file,
    onProgress
  );

  if (!assetId) {
    throw new Error('缺少上传资产 ID，无法确认上传状态');
  }

  let confirmData: Api.Upload.ConfirmUploadResponse;
  try {
    confirmData = await fetchConfirmUpload(assetId);
  } catch (err) {
    throw new Error(err instanceof Error ? err.message : '确认 OSS 上传结果失败');
  }

  return {
    ...presignData,
    ...confirmData,
    already_uploaded: false,
    file_name: file.name,
    file_ext: getFileExtension(file.name),
    file_size: file.size,
    file_type: fileType,
    object_key: presignData.object_key || '',
    upload_asset_id: confirmData.asset_id || assetId,
    asset_id: String(confirmData.asset_id || assetId),
    file_url: presignData.file_url || '',
    status: confirmData.status,
    duration_ms: confirmData.duration_ms || metadata.duration_ms || presignData.duration_ms,
    file_width: confirmData.file_width || metadata.width || presignData.file_width,
    file_height: confirmData.file_height || metadata.height || presignData.file_height
  };
}

/**
 * 上传图片结构体对象
 */
export async function uploadImage(
  image: PreviewImage,
  scene: Api.Upload.UploadScene = 'COMMON',
  policy?: AccessPolicyRoute
): Promise<PreviewImage> {
  const file = image.file;
  if (!file) {
    return image;
  }

  const uploaded = await uploadFile(file, scene, policy);

  return {
    ...image,
    url: uploaded.file_url || image.url,
    file_ext: uploaded.file_ext,
    file_name: uploaded.file_name || image.file_name,
    file_size: uploaded.file_size || image.file_size,
    file_type: uploaded.file_type || image.file_type,
    is_object_url: false,
    object_key: uploaded.object_key,
    upload_asset_id: uploaded.upload_asset_id || uploaded.asset_id,
    asset_id: uploaded.asset_id,
    unique_id: uploaded.unique_id
  };
}

